import React, { useState, useRef, Fragment, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, TagIcon, ChevronDownIcon, ExternalLinkIcon, NewspaperIcon, TrophyIcon, InstagramIcon, FacebookIcon, PlayCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
// ============================================================
//  EASY EDITING ZONE — Add or edit blog posts here only!
// ============================================================
interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  fullStory?: string;
  category: string;
  date: string;
  author: string;
  readTime: string;
  href: string;
  image: string;
  secondImage?: string;
  imagePosition?: string;
  featured?: boolean;
  coverageLinks?: {
    label: string;
    url: string;
  }[];
  videoEmbeds?: {
    youtubeId?: string;
    facebookUrl?: string;
    label: string;
  }[];
  socialLinks?: {
    platform: 'instagram' | 'facebook';
    url: string;
    label: string;
  }[];
  players?: {
    name: string;
    number: string;
    position: string;
    team: string;
    teamColor: string;
    image: string;
    profileUrl: string;
    bio: string;
  }[];
}
const BLOG_POSTS: BlogPost[] = [{
  id: 'post-9',
  title: 'President Asfura Represents Honduras at Shield of the Americas Conference',
  excerpt: 'Honduras President Nasry Asfura joined heads of state from across the Western Hemisphere at the Shield of the Americas conference in Doral, Florida, reinforcing Honduras\u2019 commitment to regional security and cooperation.',
  fullStory: `Honduras President Nasry Asfura traveled to Doral, Florida today to participate in the Shield of the Americas conference, a landmark gathering of Western Hemisphere leaders focused on strengthening regional security, economic cooperation, and shared governance priorities. President Asfura stood alongside fellow heads of state for the official group photo and was present for the signing ceremony, underscoring Honduras\u2019 active role in shaping the future of hemispheric collaboration. The conference brought together presidents and senior officials from across Latin America and the Caribbean in one of the most significant diplomatic gatherings of the year.

For Honduras, the conference represents a meaningful opportunity to deepen ties with neighboring nations and reinforce the country\u2019s standing as a committed partner in addressing the challenges facing the Americas. President Asfura used the occasion to engage directly with regional counterparts on issues including trade, migration, public safety, and investment, all of which carry direct implications for Honduran communities both at home and in the United States. His presence at the table sends a clear signal that Honduras intends to be an active and visible participant in the conversations that will define the region\u2019s path forward.

The Honduran American community is watching these developments closely. Diplomatic engagement at this level has real consequences for the millions of Hondurans living abroad, particularly on matters of immigration policy, bilateral agreements, and economic opportunity. The Honduran American Association will continue to monitor outcomes from the Shield of the Americas conference and share updates as they become available.`,
  category: 'Political',
  date: 'March 7, 2026',
  author: 'HAA News Desk',
  readTime: '4 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/ew97atokoCc3satNApke4W/SoA_1.jpg',
  secondImage: 'https://cdn.magicpatterns.com/uploads/owEqe9jjvLJtXMAqAcWY9j/SoA_2.jpg',
  imagePosition: 'center center',
  coverageLinks: [{
    label: 'The Guardian',
    url: 'https://www.theguardian.com/us-news/2026/mar/07/trump-shield-of-americas-summit'
  }, {
    label: 'Politico',
    url: 'https://www.politico.com/news/2026/03/07/trump-military-cartels-latin-america-iran-00818070'
  }, {
    label: 'The Hill',
    url: 'https://thehill.com/homenews/administration/5772926-donald-trump-shield-of-americas-war-on-drugs-noem/'
  }, {
    label: 'La Prensa',
    url: 'https://www.laprensa.hn/honduras/podemos-sentir-america-unida-afirma-presidente-asfura-cumbre-trump-GP29626264'
  }, {
    label: 'CNN Español',
    url: 'https://cnnespanol.cnn.com/2026/03/07/eeuu/coalicion-militar-mensajes-venezuela-mexico-cuba-claves-cumbre-trump-orix'
  }]
}, {
  id: 'post-10',
  title: 'Honduras Rejoins ICSID: President Asfura Signs Adhesion in Washington',
  excerpt: 'President Nasry Asfura traveled to Washington, D.C. to formally sign Honduras\u2019 adhesion to the ICSID Convention, marking the country\u2019s return to the International Centre for Settlement of Investment Disputes and sending a powerful signal to global investors.',
  fullStory: `In a significant step toward restoring international investor confidence, Honduras President Nasry Asfura traveled to Washington, D.C. on March 6 to formally sign the country\u2019s adhesion to the Convention on the Settlement of Investment Disputes between States and Nationals of Other States, commonly known as the ICSID Convention. The signing ceremony took place at the World Bank Group headquarters, where President Asfura was joined by senior officials from ICSID and the World Bank.

ICSID, the International Centre for Settlement of Investment Disputes, is an arm of the World Bank Group and serves as the world\u2019s leading institution for international investment dispute resolution. Membership in ICSID provides foreign investors with a trusted, neutral forum to resolve disputes with host governments\u2014a critical safeguard that encourages cross-border investment and economic development. Honduras had previously withdrawn from the convention, a move that raised concerns among the international business community about the country\u2019s commitment to protecting foreign investment.

President Asfura\u2019s decision to rejoin ICSID represents a clear reversal of that course and a deliberate effort to reposition Honduras as an open, reliable destination for international capital. By signing the adhesion, Honduras is signaling to multinational corporations, development banks, and foreign governments that it is prepared to play by the rules of the global investment framework and to offer the legal protections that serious investors require.

For the Honduran-American community, this development carries real significance. A stronger investment climate in Honduras means more jobs, better infrastructure, and expanded economic opportunity for families on both sides of the border. Diaspora entrepreneurs and investors who have long sought to channel resources back to Honduras now have an additional layer of institutional assurance that their investments will be protected under international law.

The Honduran-American Association views this as a positive and forward-looking step. Rejoining ICSID is not just a legal formality\u2014it is a statement of intent. It tells the world that Honduras is serious about governance, accountability, and creating the conditions for sustainable growth. We will continue to track how this decision translates into tangible outcomes for Honduran communities at home and abroad.`,
  category: 'Business',
  date: 'March 6, 2026',
  author: 'HAA News Desk',
  readTime: '5 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/bGuGogi7aKTRBxk7ELEbiK/Ciadi_1.jpg',
  secondImage: 'https://cdn.magicpatterns.com/uploads/sfS2AUPFqjBTkVDDBtUpeF/Ciadi_2.jpg',
  imagePosition: 'center center',
  coverageLinks: [{
    label: 'La Tribuna',
    url: 'https://www.latribuna.hn/2026/03/06/nasry-asfura-firma-adhesion-de-honduras-al-convenio-del-ciadi-en-washington/'
  }, {
    label: 'El Heraldo',
    url: 'https://www.elheraldo.hn/honduras/honduras-regresa-ciadi-presidente-nasry-asfura-firma-adhesion-washington-estados-unidos-FM29612061'
  }]
}, {
  id: 'post-11',
  title: 'President Asfura Signs USTDA Agreement to Study Honduras Interoceanic Railway',
  excerpt: 'President Nasry Asfura signed an agreement with the U.S. Trade and Development Agency (USTDA) in Washington to fund a feasibility study for Honduras\u2019 proposed interoceanic railway, a transformative infrastructure project aimed at diversifying Western Hemisphere supply chain routes.',
  fullStory: `Honduras President Nasry Asfura visited Washington, D.C. to sign a landmark agreement with the U.S. Trade and Development Agency (USTDA), securing U.S. support for a feasibility study of Honduras\u2019 proposed interoceanic railway. The agreement was signed at USTDA headquarters, where President Asfura was hosted by agency leadership in a ceremony that underscored the growing economic partnership between the United States and Honduras.

The interoceanic railway project envisions a rail corridor connecting Honduras\u2019 Atlantic and Pacific coasts, creating a new logistics route that could serve as an alternative to existing transit options in the region. If realized, the railway would position Honduras as a critical node in Western Hemisphere trade, offering multinational shippers and manufacturers a faster, more diversified path for moving goods between oceans. The USTDA-funded feasibility study will assess the technical, financial, and environmental viability of the project, laying the groundwork for potential future investment and construction.

For the United States, the agreement aligns with broader strategic goals to diversify supply chain routes and reduce dependence on any single corridor in the Americas. USTDA\u2019s involvement signals that Washington sees Honduras as a credible partner for large-scale infrastructure development and a country whose geographic position offers genuine value to hemispheric commerce. The agency\u2019s mission is to connect U.S. businesses with export opportunities in emerging markets, and the Honduras railway study fits squarely within that mandate.

For Honduras, the stakes are enormous. A functioning interoceanic railway would generate thousands of jobs during construction and operation, attract foreign direct investment, stimulate economic activity in underserved regions along the route, and fundamentally reshape the country\u2019s role in global trade. It would also provide a powerful counternarrative to the challenges that have defined Honduras in international headlines, demonstrating that the country is capable of executing ambitious, forward-looking infrastructure projects with the backing of the world\u2019s largest economy.

The Honduran-American community stands to benefit directly from this kind of bilateral economic engagement. Stronger trade ties between the U.S. and Honduras create opportunities for diaspora entrepreneurs, investors, and professionals who operate across both countries. The Honduran-American Association welcomes this agreement as a concrete step toward the kind of economic development that lifts communities on both sides of the border, and we will continue to follow the project\u2019s progress as the feasibility study moves forward.`,
  category: 'Business',
  date: 'March 5, 2026',
  author: 'HAA News Desk',
  readTime: '5 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/iJ4t8AL121gTHUu1xzzQx4/USTDA.jpg',
  secondImage: 'https://cdn.magicpatterns.com/uploads/789jgLnfefG9M3X18mfmSb/USTDA_2.jpg',
  imagePosition: 'center 30%',
  coverageLinks: [{
    label: 'Radio América',
    url: 'https://www.radioamerica.hn/asfura-firma-acuerdo-con-ustda-para-estudio-de-factibilidad-del-ferrocarril-interoceanico-de-honduras/'
  }, {
    label: 'La Prensa',
    url: 'https://www.laprensa.hn/honduras/presidente-asfura-firma-acuerdo-estudio-ferrocarril-interoceanico-honduras-FN29601172'
  }, {
    label: 'USTDA',
    url: 'https://www.ustda.gov/ustda-hosts-honduran-president-signs-agreement-to-diversify-u-s-supply-chain-routes-in-the-western-hemisphere/'
  }]
}, {
  id: 'post-12',
  title: 'Wabafu Garifuna Dance Theater: Keeping Honduran Culture Alive Through Movement',
  excerpt: 'The Wabafu Garifuna Dance Theater is preserving and sharing the rich cultural traditions of the Garifuna people through powerful performances that blend ancestral rhythms, storytelling, and dance rooted in Honduras\u2019 Caribbean coast.',
  fullStory: `The Wabafu Garifuna Dance Theater stands as one of the most vibrant expressions of Honduran cultural heritage in the United States. Founded to preserve and celebrate the traditions of the Garifuna people \u2014 an Afro-Indigenous community with deep roots along Honduras\u2019 Caribbean coast \u2014 the company brings ancestral rhythms, sacred dances, and powerful storytelling to stages across the country.

Through performances that blend traditional punta, paranda, and Yankunu with contemporary choreography, Wabafu creates an immersive experience that transports audiences to the shores of Honduras. Their work is more than entertainment; it is cultural preservation in motion. Each performance carries forward the music, language, and spiritual practices of a community whose traditions are recognized by UNESCO as Intangible Cultural Heritage of Humanity.

For the Honduran-American diaspora, Wabafu represents something deeply meaningful: a living connection to home. Their performances remind audiences \u2014 especially younger generations born in the United States \u2014 of the richness and resilience of Garifuna culture. The company has performed at cultural festivals, universities, and community events, earning recognition as a vital bridge between Honduras\u2019 Afro-Caribbean heritage and the broader American cultural landscape.

The Honduran-American Association is proud to spotlight Wabafu Garifuna Dance Theater as an example of the artistic excellence and cultural pride that defines our community. Their dedication to keeping Garifuna traditions alive ensures that the stories, sounds, and movements of Honduras\u2019 Caribbean coast will continue to inspire for generations to come.`,
  category: 'Arts & Entertainment',
  date: 'March 7, 2026',
  author: 'HAA Arts Desk',
  readTime: '4 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/a79cX8GF9Z8Zr2wnP6ymup/Garifuna-Collective-1200x628.jpg',
  imagePosition: 'center top',
  videoEmbeds: [{
    youtubeId: '6gE-_HXPyjc',
    label: 'Wabafu Performance'
  }, {
    youtubeId: 'YYPCa02J3hg',
    label: 'Cultural Showcase'
  }, {
    youtubeId: 'S94uxfMkwGI',
    label: 'Garifuna Rhythms'
  }],
  socialLinks: [{
    platform: 'instagram',
    url: 'https://www.instagram.com/garifunadancecompany/',
    label: 'Instagram'
  }, {
    platform: 'facebook',
    url: 'https://www.facebook.com/wabafudance/',
    label: 'Facebook'
  }]
}, {
  id: 'post-1',
  title: 'New Ambassador to the USA is sworn in',
  excerpt: "Roberto Flores Bermudez has been sworn in as Honduras's new ambassador to the United States, a move the Honduran government says is aimed at deepening cooperation with Washington...",
  fullStory: "Roberto Flores Bermudez has been sworn in as Honduras's new ambassador to the United States, a move the Honduran government says is aimed at deepening cooperation with Washington and advancing a bilateral agenda shaped by migration, investment, and economic priorities. Foreign Minister Mireya Aguero administered the oath in an official ceremony in Tegucigalpa, with government statements framing the appointment as part of a push to strengthen ties with the U.S. while prioritizing services and protection for the Honduran community living abroad.\n\nA veteran career diplomat, Flores Bermudez brings decades of experience in both bilateral and multilateral posts, including prior service as Honduras's ambassador to the United States and senior roles at the United Nations. International profiles note he joined the Honduran foreign service in the late 1970s and has held top-level assignments that span Washington and key European postings, positioning him as a seasoned interlocutor at a moment when Honduras-U.S. relations remain closely tied to migration management, trade, and regional cooperation.",
  category: 'Political',
  date: 'February 28, 2026',
  author: 'HAA News Desk',
  readTime: '4 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/jiGXG6xB7qfM8n3G59eizj/rflores_Ambassador.jpg',
  featured: true,
  coverageLinks: [{
    label: 'La Tribuna',
    url: 'https://www.latribuna.hn/2026/03/02/embajador-flores-prioriza-apoyo-a-tepesianos-y-buenas-condiciones-arancelarias-en-washington/'
  }, {
    label: 'La Prensa',
    url: 'https://www.laprensa.hn/honduras/roberto-flores-bermudez-nuevo-embajador-honduras-estados-unidos-MG29493395'
  }, {
    label: 'El Heraldo',
    url: 'https://www.elheraldo.hn/honduras/juramentan-roberto-flores-bermudez-embajador-honduras-estados-unidos-OG29493827'
  }]
}, {
  id: 'post-7',
  title: '',
  excerpt: "A federal court ruling in February dealt a significant blow to Honduran TPS holders in the United States, upholding the administration's authority to terminate the program and leaving hundreds of thousands of Hondurans facing an uncertain future.",
  fullStory: `On February 9, 2026, a federal court ruled against Honduran Temporary Protected Status (TPS) holders, upholding the administration's decision to terminate protections for one of the largest TPS-designated populations in the United States. The ruling sent shockwaves through Honduran communities across the country, where an estimated 80,000 to 100,000 individuals have relied on TPS to live and work legally for years — in some cases, for decades.\n\nTPS for Honduras was originally designated in 1999 following the catastrophic destruction caused by Hurricane Mitch. Since then, it has been repeatedly extended, allowing Honduran nationals to build lives, raise families, and contribute to their communities across the United States. Many TPS holders are parents of U.S.-born children, homeowners, small business owners, and essential workers in industries ranging from construction to healthcare.\n\nThe court's February ruling rejected arguments that the termination was arbitrary or violated due process, siding with the government's position that TPS designations are discretionary and subject to executive authority. Advocates and legal organizations have vowed to appeal, but the path forward remains uncertain.\n\nFor the Honduran-American Association, this ruling is not just a legal setback — it is a deeply personal one. The individuals affected are our neighbors, our family members, and the backbone of our community. Many have lived in the United States for 20 or more years. Their ties to Honduras have faded; their roots here run deep. Forcing their removal would not only devastate families but would also strip communities across the country of people who have given so much.\n\nWe stand firmly with TPS holders and call on Congress to act. A legislative solution — one that provides a permanent pathway to legal status for long-term TPS holders — is both morally necessary and practically urgent. The Honduran-American Association will continue to advocate, organize, and amplify the voices of those affected. We will not be silent, and we will not stop fighting until every member of our community has the security and dignity they deserve.\n\nIf you or someone you know is a TPS holder and needs guidance, we encourage you to connect with a qualified immigration attorney and stay informed through trusted legal organizations. Resources are available, and you do not have to navigate this alone.`,
  category: 'Immigration',
  date: 'February 9, 2026',
  author: 'Jay Miranda',
  readTime: '5 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/75m7ELyNuDt8sj37WnjoPs/daca_BC_056_111219-4.jpg',
  imagePosition: 'center center',
  coverageLinks: [{
    label: 'DW Español',
    url: 'https://www.dw.com/es/honduras-lamenta-fallo-en-eeuu-que-cancela-tps/a-75888020'
  }, {
    label: 'Univision',
    url: 'https://www.univision.com/noticias/estados-unidos/kristi-noem-anuncia-fallo-judicial-que-permite-cancelar-el-tps-para-honduras-nicaragua-y-nepal'
  }, {
    label: 'Reuters',
    url: 'https://www.reuters.com/legal/government/us-appeals-court-lets-trump-continue-ending-deportation-protections-2026-02-09/'
  }, {
    label: 'La Prensa',
    url: 'https://www.laprensa.hn/honduras/cancelan-nuevamente-tps-ciudadanos-honduras-nicaragua-nepal-BK29233398'
  }]
}, {
  id: 'post-2',
  title: 'Marvin B. Figueroa Becomes the Highest ranking Honduran-American in U.S. Political office',
  excerpt: 'Newly selected by Governor Spanberger to serve in her administration, Marvin B. Figueroa, an Arlington resident with Garifuna Honduran roots from La Ceiba, has built a career at the intersection of health policy and public service, focusing on practical reforms that help families afford care and navigate government programs.',
  fullStory: `Newly selected by Governor Spanberger to serve in her administration, Marvin B. Figueroa, an Arlington resident with Garifuna Honduran roots from La Ceiba, has built a career at the intersection of health policy and public service, focusing on practical reforms that help families afford care and navigate government programs.\n\nFigueroa currently serves as a Vice President at BGR Group, where he leads work on federal and state health policy. Across the private and public sectors, his work has centered on expanding access to affordable care, strengthening programs that support families, and improving how government delivers services in ways that meet people where they are. Those who have worked alongside him describe an approach shaped by a simple premise: policy should widen opportunity and give every person a fair shot.\n\nHis national experience includes senior roles across the federal government, where he advanced bipartisan efforts to improve health outcomes and modernize public policy. As a senior official at the U.S. Department of Health and Human Services, Figueroa worked with governors, state agencies, and local leaders to carry out national priorities and strengthen the delivery of essential services. Earlier, as Health Policy Advisor to U.S. Senator Mark Warner, he helped craft and pass the CHRONIC Care Act, legislation aimed at improving care for individuals with complex medical needs and strengthening Medicare's ability to support people living with chronic conditions. He later became a Chief of Staff in the U.S. Senate, bringing policy and management experience to the day to day work of governing.\n\nIn Virginia, Figueroa served as Deputy Secretary of Health and Human Resources under Governor Ralph Northam, collaborating with agency leaders, health systems, and community partners on initiatives to expand Medicaid, broaden behavioral health supports, strengthen social services, and improve public health operations. Supporters of that work say it helped expand the Commonwealth's capacity to deliver services across rural and urban communities, while reinforcing systems meant to be dependable and aligned with residents' needs.\n\nFigueroa's selection also brings added visibility to the Garifuna and Honduran American story in Virginia and beyond, linking public leadership to cultural heritage rooted on Honduras' northern coast. He is a graduate of the University of Virginia Sorensen Political Leaders Program and Lead Virginia. A POSSE Foundation Scholar, he earned degrees from Vanderbilt University and Harvard University. He lives in Arlington and has remained active in the Commonwealth he has long called home, with an emphasis on listening first, building partnerships, and focusing on results that people can see and feel at the kitchen table.`,
  category: 'Note Worthy',
  date: 'March 1, 2026',
  author: 'Jay Miranda',
  readTime: '4 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/mrfShjrpgdKKhd4SXuv4rX/Screenshot_2026-03-02_at_11.13.16_PM.png',
  coverageLinks: [{
    label: 'Virginia Mercury',
    url: 'https://virginiamercury.com/2026/03/02/virginias-new-public-health-leaders-take-the-helm-as-state-agencies-weather-uncharted-waters/'
  }, {
    label: 'WRIC',
    url: 'https://www.wric.com/news/politics/capitol-connection/who-are-spanbergers-cabinet-picks/'
  }]
}, {
  id: 'post-5',
  title: 'Honduran-American Association present at LULAC Awards Gala',
  excerpt: 'Board Members Jay Miranda and Mario Ramos represented the Honduran-American Association at the LULAC Gala in Washington, D.C. on February 24, joining Latino leaders from across the country for an evening centered on civic leadership, advocacy, and opportunity.',
  fullStory: `Board Members Jay Miranda and Mario Ramos represented the Honduran-American Association at the LULAC Gala in Washington, D.C. on February 24, joining Latino leaders from across the country for an evening centered on civic leadership, advocacy, and opportunity. As the nation's largest and oldest Latino civil rights organization, LULAC has long played a key role in advancing educational access, economic mobility, and fair treatment for Latino communities―values that closely align with the Honduran-American Association's mission to elevate our diaspora and build stronger bridges between the United States and Honduras. Their presence underscored the Association's commitment to being at the table where national conversations are happening, strengthening relationships with partners and allies, and ensuring Honduran-American voices are included in the broader movement for Latino representation and progress.`,
  category: 'Events',
  date: 'February 24, 2026',
  author: 'Jay Miranda',
  readTime: '3 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/qK2Q5NDctMNP45cZYr9udX/IMG_3984.jpg'
}, {
  id: 'post-6',
  title: 'Board Member Sindy Benavides represents the Latino Voice in the Telemundo State of Union Debate',
  excerpt: "HAA Board Member Sindy Benavides joined Telemundo's live coverage of the State of the Union address, representing the Latino community's perspective on the issues that matter most to families across the country.",
  fullStory: `HAA Board Member Sindy Benavides joined Telemundo's live coverage of the State of the Union address, representing the Latino community's perspective on the issues that matter most to families across the country. Speaking from Washington, D.C. with the Capitol as a backdrop, Benavides brought a clear and grounded voice to the national conversation, addressing immigration, economic opportunity, and the priorities of Latino households in a moment of significant political attention.\n\nBenavides has long been a prominent advocate for Latino civic engagement and community empowerment. Her appearance on Telemundo's State of the Union coverage reflects the growing visibility of Honduran-American leaders in national media and policy discussions. The Honduran-American Association is proud to have her voice at the table, ensuring that the stories and concerns of our community are heard at the highest levels of public discourse.`,
  category: 'Note Worthy',
  date: 'March 2, 2026',
  author: 'Jay Miranda',
  readTime: '3 min read',
  href: '#',
  image: 'https://cdn.magicpatterns.com/uploads/bo3zBba1TsMyKsx9xDZJx6/Screenshot_2026-03-02_at_11.56.16_PM.png',
  imagePosition: 'center center'
}, {
  id: 'post-8',
  title: '',
  excerpt: 'From Inter Miami to D.C. United, Honduran talent continues to make its mark in Major League Soccer. Here is a look at the five Honduran players currently on MLS rosters for the 2025–26 season.',
  fullStory: `Honduras has a proud tradition of producing world-class soccer talent, and the 2025–26 MLS season is no exception. Five players of Honduran descent are currently representing La H across Major League Soccer, carrying the blue and white flag into stadiums from Miami to Nashville. Spanning generations, from a 15-year-old prodigy to seasoned internationals, they represent the depth and diversity of Honduran soccer talent. Their presence in MLS is a source of pride for the entire Honduran-American community and a reminder that La H continues to punch above its weight on the world stage. Click on each player below to learn more about their journey.`,
  category: 'Sports',
  date: 'March 6, 2026',
  author: 'HAA Sports Desk',
  readTime: '6 min read',
  href: '#',
  image: 'https://images.mlssoccer.com/image/private/t_editorial_landscape_12_desktop_2x/mls/y1yoswqgpbdlg0squfdl.jpg',
  imagePosition: 'center 30%',
  players: [{
    name: 'Oscar Avilez',
    number: '32',
    position: 'Midfielder',
    team: 'D.C. United',
    teamColor: 'bg-[#231f20]',
    image: 'https://cdn.magicpatterns.com/uploads/ekDNV6arCYSCoQPRAVhQQ6/Oscar_AViles.png',
    profileUrl: 'https://www.dcunited.com/players/oscar-avilez/',
    bio: "At just 15 years old, Oscar Avilez is the youngest Honduran in MLS and one of the most exciting prospects in the league. Born in Choluteca, Honduras, the 5'8\" midfielder signed with D.C. United as a supplemental roster player, signaling the club's belief in his enormous potential. Avilez represents the future — not just for Honduran soccer, but for the growing pipeline of Central American talent entering MLS at younger and younger ages."
  }, {
    name: 'David Ruíz',
    number: '41',
    position: 'Midfielder',
    team: 'Inter Miami CF',
    teamColor: 'bg-[#231f20]',
    image: 'https://cdn.magicpatterns.com/uploads/9DS6NCuDXNEY4AbRq4yZ39/David_ruiz.png',
    profileUrl: 'https://www.intermiamicf.com/players/david-ruiz/',
    bio: "Born in Miami to Honduran roots, David Ruíz is a 22-year-old homegrown midfielder who has risen through Inter Miami's academy system. At 5'10\", Ruíz brings technical skill and vision to the midfield, earning his place on a roster that includes some of the biggest names in world football. His journey from the Miami youth ranks to the first team is a testament to the talent pipeline within the Honduran-American community."
  }, {
    name: 'Joseph Rosales',
    number: '30',
    position: 'Midfielder',
    team: 'Austin FC',
    teamColor: 'bg-[#00B140]',
    image: 'https://cdn.magicpatterns.com/uploads/jiBswYV5Z7LytHTmKaZcsp/Joseph_Rosales.png',
    profileUrl: 'https://www.austinfc.com/players/joseph-rosales/',
    bio: 'Born in Tegucigalpa, Honduras, Joseph Rosales is a 25-year-old left-footed midfielder who has established himself as a key contributor at Austin FC. Standing 5\'11" and weighing 161 lbs, Rosales brings creativity and composure on the ball. A senior roster player, he represents the next generation of Honduran midfield talent making waves in MLS.'
  }, {
    name: 'Andy Nájar',
    number: '31',
    position: 'Defender',
    team: 'Nashville SC',
    teamColor: 'bg-[#1F1646]',
    image: 'https://cdn.magicpatterns.com/uploads/gj7vYKrAwDrW6rKF2djByG/Any_Najar.png',
    profileUrl: 'https://www.nashvillesc.com/players/andy-najar/',
    bio: "A veteran of both MLS and European football, Andy Nájar is one of the most decorated Honduran players in recent history. Born in Choluteca, Honduras, the 32-year-old defender previously starred for D.C. United and Anderlecht in Belgium's top division before returning to MLS with Nashville SC. At 5'8\" and 150 lbs, Nájar's experience, defensive intelligence, and versatility make him a valuable presence on and off the pitch."
  }, {
    name: 'Bryan Acosta',
    number: '6',
    position: 'Midfielder',
    team: 'Nashville SC',
    teamColor: 'bg-[#1F1646]',
    image: 'https://cdn.magicpatterns.com/uploads/4wpfU13ik3hQp2hKLHsjJR/Bryan_Acosta.png',
    profileUrl: 'https://www.nashvillesc.com/players/bryan-acosta/',
    bio: "Bryan Acosta is a 32-year-old midfielder born in La Ceiba, Honduras. A senior roster player standing 5'9\" and 176 lbs, Acosta is a seasoned Honduran international who has represented his country at the highest levels, including World Cup qualifiers and the Gold Cup. His right-footed passing range and leadership in midfield bring invaluable experience to Nashville's squad."
  }],
  coverageLinks: [{
    label: 'Inter Miami CF',
    url: 'https://www.intermiamicf.com/players/david-ruiz/'
  }, {
    label: 'Austin FC',
    url: 'https://www.austinfc.com/players/joseph-rosales/'
  }, {
    label: 'Nashville SC',
    url: 'https://www.nashvillesc.com/players/andy-najar/'
  }, {
    label: 'Nashville SC',
    url: 'https://www.nashvillesc.com/players/bryan-acosta/'
  }, {
    label: 'D.C. United',
    url: 'https://www.dcunited.com/players/oscar-avilez/'
  }]
}];
// ============================================================
//  End of editing zone. Component code below.
// ============================================================
const ALL_CATEGORIES = ['All', 'Political', 'Immigration', 'Business', 'Note Worthy', 'Events', 'Arts & Entertainment', 'Sports'];
const CATEGORY_COLORS: Record<string, string> = {
  Political: 'bg-haa-blue text-white',
  Immigration: 'bg-haa-skyblue text-white',
  Business: 'bg-emerald-600 text-white',
  'Note Worthy': 'bg-haa-midblue text-white',
  'Arts & Entertainment': 'bg-haa-navy text-white',
  Events: 'bg-blue-400 text-white',
  Sports: 'bg-orange-500 text-white'
};
const GRADIENT_FALLBACKS: Record<string, string> = {
  Political: 'from-haa-blue via-blue-700 to-haa-navy',
  Immigration: 'from-haa-skyblue to-haa-midblue',
  Business: 'from-emerald-500 to-emerald-800',
  'Note Worthy': 'from-haa-midblue to-haa-navy',
  'Arts & Entertainment': 'from-haa-navy to-blue-950',
  Events: 'from-blue-400 to-haa-midblue',
  Sports: 'from-orange-500 to-red-600'
};
function getCategoryColor(category: string) {
  return CATEGORY_COLORS[category] ?? 'bg-haa-blue text-white';
}
function getGradient(category: string) {
  return GRADIENT_FALLBACKS[category] ?? 'from-haa-blue to-haa-navy';
}
// Featured Card
function FeaturedCard({
  post
}: {
  post: BlogPost;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const [storyOpen, setStoryOpen] = useState(false);
  const {
    t
  } = useLanguage();
  const fullStory = t('news.featured.fullStory');
  const teaser = t('news.featured.teaser');
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 40
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-xl lg:col-span-2 group" data-id="element-1889">
      <div className="relative h-64 sm:h-80 overflow-hidden" data-id="element-1890">
        {post.image ? <img src={post.image} alt={post.title} className="w-full h-full object-cover object-[center_25%] transition-transform duration-700 group-hover:scale-105" data-id="element-1891" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-1892">
            <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 30% 70%, white 1px, transparent 1px)',
          backgroundSize: '25px 25px'
        }} data-id="element-1893" />
          </div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-1894" />
        <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1895">
          <span className={`font-nunito text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${getCategoryColor(post.category)} mb-3 inline-block`} data-id="element-1896">
            {t('news.catPolitical')}
          </span>
          <p className="font-bebas text-white text-3xl sm:text-4xl leading-tight tracking-wider line-clamp-2" data-id="element-1897">
            {t('news.featured.headline')}
          </p>
        </div>
      </div>

      <div className="p-6 sm:p-8" data-id="element-1898">
        <div className="flex items-center gap-3 mb-4" data-id="element-1899">
          <div className="w-8 h-1 bg-haa-blue rounded-full" data-id="element-1900" />
          <span className="font-nunito text-haa-blue text-xs font-bold uppercase tracking-widest" data-id="element-1901">
            {t('news.featuredStory')}
          </span>
        </div>

        <h2 className="font-bebas text-3xl sm:text-4xl text-haa-navy tracking-wider leading-tight mb-3" data-id="element-1902">
          {t('news.featured.title')}
        </h2>

        <div className="mb-6" data-id="element-1903">
          {!storyOpen && <p className="font-nunito text-gray-600 leading-relaxed" data-id="element-1904">
              {teaser}
            </p>}

          <AnimatePresence initial={false} data-id="element-1905">
            {storyOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} className="overflow-hidden" data-id="element-1906">
                <p className="font-nunito text-gray-600 leading-relaxed mt-3 whitespace-pre-line" data-id="element-1907">
                  {fullStory}
                </p>
              </motion.div>}
          </AnimatePresence>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3" data-id="element-1908">
            <button onClick={() => setStoryOpen(!storyOpen)} className="inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-sm hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded" aria-expanded={storyOpen} data-id="element-1909">
              {storyOpen ? t('news.collapseStory') : t('news.readFullStory')}
              <motion.span animate={{
              rotate: storyOpen ? 180 : 0
            }} transition={{
              duration: 0.25
            }} className="inline-flex" data-id="element-1910">
                <ArrowRightIcon className="w-4 h-4 rotate-90" data-id="element-1911" />
              </motion.span>
            </button>

            <span className="text-gray-200 select-none hidden sm:inline" data-id="element-1912">
              |
            </span>

            <div className="flex items-center gap-2" data-id="element-1913">
              <NewspaperIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-1914" />
              <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-1915">
                {t('news.alsoCoveredBy')}
              </span>
              <a href="https://www.latribuna.hn/2026/03/02/embajador-flores-prioriza-apoyo-a-tepesianos-y-buenas-condiciones-arancelarias-en-washington/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-1916">
                La Tribuna
                <ExternalLinkIcon className="w-3 h-3" data-id="element-1917" />
              </a>
              <span className="text-gray-200 select-none" data-id="element-1918">·</span>
              <a href="https://www.laprensa.hn/honduras/roberto-flores-bermudez-nuevo-embajador-honduras-estados-unidos-MG29493395" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-1919">
                La Prensa
                <ExternalLinkIcon className="w-3 h-3" data-id="element-1920" />
              </a>
              <span className="text-gray-200 select-none" data-id="element-1921">·</span>
              <a href="https://www.elheraldo.hn/honduras/juramentan-roberto-flores-bermudez-embajador-honduras-estados-unidos-OG29493827" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-1922">
                El Heraldo
                <ExternalLinkIcon className="w-3 h-3" data-id="element-1923" />
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-1924">
          <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm" data-id="element-1925">
            <span className="flex items-center gap-1.5" data-id="element-1926">
              <CalendarIcon className="w-4 h-4 text-haa-blue" data-id="element-1927" />
              <span className="font-nunito" data-id="element-1928">{t('news.featured.date')}</span>
            </span>
          </div>
        </div>
      </div>
    </motion.article>;
}
// Regular Card
function PostCard({
  post,
  index
}: {
  post: BlogPost;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [storyOpen, setStoryOpen] = useState(false);
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const postIdMap: Record<string, string> = {
    'post-1': '1',
    'post-7': '7',
    'post-2': '2',
    'post-5': '5',
    'post-6': '6',
    'post-8': '8',
    'post-9': '9',
    'post-10': '10',
    'post-11': '11',
    'post-12': '12'
  };
  const postNum = postIdMap[post.id] ?? '';
  const translatedTitle = postNum ? t(`news.post${postNum}.title`) : post.title;
  const translatedExcerpt = postNum ? t(`news.post${postNum}.excerpt`) : post.excerpt;
  const dateKey = postNum ? `news.post${postNum}.date` : '';
  const dateTranslation = dateKey ? t(dateKey) : '';
  const translatedDate = dateKey && dateTranslation !== dateKey ? dateTranslation : post.date;
  const readTimeKey = postNum ? `news.post${postNum}.readTime` : '';
  const readTimeTranslation = readTimeKey ? t(readTimeKey) : '';
  const translatedReadTime = readTimeKey && readTimeTranslation !== readTimeKey ? readTimeTranslation : post.readTime;
  const fullStoryKey = postNum ? `news.post${postNum}.fullStory` : '';
  const fullStoryTranslation = fullStoryKey ? t(fullStoryKey) : '';
  const translatedFullStory = fullStoryKey && fullStoryTranslation !== fullStoryKey ? fullStoryTranslation : post.fullStory;
  const isSportsComposite = post.id === 'post-8' || post.players && post.players.length > 0;
  const isWabafu = post.id === 'post-12';
  const compositeImages = [{
    name: 'Oscar Avilez',
    url: 'https://cdn.magicpatterns.com/uploads/hkEmYVyvz28kSzeT5ukNrR/Oscar_AViles.png'
  }, {
    name: 'David Ruíz',
    url: 'https://cdn.magicpatterns.com/uploads/jX1nHu27oh4QsStqz4qtmu/David_ruiz.png'
  }, {
    name: 'Joseph Rosales',
    url: 'https://cdn.magicpatterns.com/uploads/qGfb5k5pbqZyGMPg5qDGSp/Joseph_Rosales.png'
  }, {
    name: 'Andy Nájar',
    url: 'https://cdn.magicpatterns.com/uploads/mQoPkEXd6jyhojvo6LYyV9/Any_Najar.png'
  }, {
    name: 'Bryan Acosta',
    url: 'https://cdn.magicpatterns.com/uploads/3zNFYQqGHoBrp1dCyKXGuJ/Bryan_Acosta.png'
  }];
  const hasMultipleImages = !!post.secondImage;
  return <motion.article ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} className="bg-white rounded-2xl overflow-hidden shadow-lg group flex flex-col" data-id="element-1929">
      {isSportsComposite ? <div className="relative h-64 overflow-hidden flex-shrink-0 bg-gradient-to-b from-haa-midblue via-haa-blue to-haa-navy" data-id="element-1930">
          {/* Player Images */}
          <div className="absolute inset-0 flex items-end justify-center px-2 pb-12 sm:pb-10" data-id="element-1931">
            {compositeImages.map((img, i) => <motion.div key={img.name} initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2 + i * 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative w-1/5 -mx-1.5 sm:-mx-2" data-id="element-1932">
                <img src={img.url} alt={img.name} className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-1933" />
              </motion.div>)}
          </div>
          {/* Gradient Overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-haa-navy/80 to-transparent" data-id="element-1934" />
          {/* Top Left Badge */}
          <div className="absolute top-3 left-3" data-id="element-1935">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-1936">
              {post.category}
            </span>
          </div>
          {/* Bottom Text */}
          <div className="absolute bottom-0 left-0 right-0 p-5 pb-3" data-id="element-1937">
            <p className="text-white/90 font-nunito text-[10px] sm:text-xs font-bold tracking-widest mb-1 flex items-center gap-1.5" data-id="element-1938">
              <span className="text-lg leading-none" data-id="element-1939">🇭🇳</span>
              {isEs ? 'HONDUREÑOS EN LA MLS · TEMPORADA 2025-26' : 'HONDURANS IN MLS · 2025-26 SEASON'}
            </p>
          </div>
        </div> : isWabafu ? <div className="relative h-52 overflow-hidden flex-shrink-0" data-id="element-1940">
          <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center center'
      }} data-id="element-1941" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-1942" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-1943">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={inView ? {
          scale: 1,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-1944">
              <span className="text-5xl mb-3 block" data-id="element-1945">🥁</span>
              <p className="font-bebas text-white text-2xl sm:text-3xl tracking-wider leading-tight" data-id="element-1946">
                {isEs ? 'CULTURA EN MOVIMIENTO' : 'CULTURE IN MOTION'}
              </p>
              <p className="font-nunito text-white/70 text-xs tracking-widest uppercase mt-1" data-id="element-1947">
                Wabafu Garifuna Dance Theater
              </p>
            </motion.div>
          </div>
          <div className="absolute top-3 left-3" data-id="element-1948">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-1949">
              {post.category}
            </span>
          </div>
        </div> : hasMultipleImages ? <div className="flex-shrink-0" data-id="element-1950">
          <div className="grid grid-cols-2 gap-0.5" data-id="element-1951">
            <div className="relative h-36 overflow-hidden" data-id="element-1952">
              <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: post.imagePosition ?? 'center 15%'
          }} data-id="element-1953" />
            </div>
            <div className="relative h-36 overflow-hidden" data-id="element-1954">
              <img src={post.secondImage!} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
            objectPosition: 'center center'
          }} data-id="element-1955" />
            </div>
          </div>
          <div className="relative -mt-8 px-3 pb-0 z-10" data-id="element-1956">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-1957">
              {post.category}
            </span>
          </div>
        </div> : <div className="relative h-44 overflow-hidden flex-shrink-0" data-id="element-1958">
          {post.image ? <img src={post.image} alt={translatedTitle} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{
        objectPosition: post.imagePosition ?? 'center 15%'
      }} data-id="element-1959" /> : <div className={`w-full h-full bg-gradient-to-br ${getGradient(post.category)}`} data-id="element-1960">
              <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-1961" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-1962" />
          <div className="absolute top-3 left-3" data-id="element-1963">
            <span className={`font-nunito text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(post.category)}`} data-id="element-1964">
              {post.category}
            </span>
          </div>
        </div>}

      <div className="p-5 flex flex-col flex-1" data-id="element-1965">
        <div className="flex items-center gap-2 mb-2" data-id="element-1966">
          <div className="w-4 h-0.5 bg-haa-blue rounded-full" data-id="element-1967" />
          <span className="font-nunito text-gray-400 text-xs" data-id="element-1968">
            {translatedDate}
          </span>
        </div>

        {!isSportsComposite && <h3 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight mb-2" data-id="element-1969">
            {translatedTitle}
          </h3>}

        <div className="flex-1 mb-3" data-id="element-1970">
          {!storyOpen && !isSportsComposite && <p className="font-nunito text-gray-500 text-sm leading-relaxed line-clamp-2" data-id="element-1971">
              {translatedExcerpt}
            </p>}

          <AnimatePresence initial={false} data-id="element-1972">
            {storyOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.35,
            ease: [0.16, 1, 0.3, 1]
          }} className="overflow-hidden" data-id="element-1973">
                {translatedFullStory && <p className="font-nunito text-gray-500 text-sm leading-relaxed mt-3 whitespace-pre-line" data-id="element-1974">
                    {translatedFullStory}
                  </p>}
                {post.players && post.players.length > 0 && <div className="space-y-2 mt-3 mb-4" data-id="element-1975">
                    {post.players.map(player => {
                const isExpanded = expandedPlayer === player.name;
                const playerKeyMap: Record<string, string> = {
                  'Oscar Avilez': 'avilez',
                  'David Ruíz': 'ruiz',
                  'Joseph Rosales': 'rosales',
                  'Andy Nájar': 'najar',
                  'Bryan Acosta': 'acosta'
                };
                const playerKey = playerKeyMap[player.name] ?? '';
                const translatedBio = playerKey ? t(`news.player.${playerKey}.bio`) : player.bio;
                const translatedPosition = player.position === 'Midfielder' ? t('news.player.position.midfielder') : player.position === 'Defender' ? t('news.player.position.defender') : player.position;
                return <div key={player.name} className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50" data-id="element-1976">
                          <button onClick={() => setExpandedPlayer(isExpanded ? null : player.name)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-gray-100 transition-colors text-left" aria-expanded={isExpanded} data-id="element-1977">
                            {player.image ? <img src={player.image} alt={player.name} className="w-16 h-16 rounded-full object-cover object-top shadow-md flex-shrink-0 border-2 border-white" data-id="element-1978" /> : <div className={`w-16 h-16 rounded-full ${player.teamColor} flex items-center justify-center text-white font-nunito font-bold text-lg shadow-md flex-shrink-0`} data-id="element-1979">
                                {player.name.split(' ').map(n => n[0]).join('')}
                              </div>}
                            <div className="flex-1 min-w-0" data-id="element-1980">
                              <p className="font-nunito text-sm font-bold text-haa-navy leading-tight" data-id="element-1981">
                                {player.name}
                                <span className="text-gray-400 font-normal ml-1.5" data-id="element-1982">
                                  #{player.number}
                                </span>
                              </p>
                              <p className="font-nunito text-xs text-gray-500 mt-0.5" data-id="element-1983">
                                {translatedPosition} · {player.team}
                              </p>
                            </div>
                            <motion.span animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex-shrink-0" data-id="element-1984">
                              <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-1985" />
                            </motion.span>
                          </button>
                          <AnimatePresence initial={false} data-id="element-1986">
                            {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.25,
                      ease: [0.16, 1, 0.3, 1]
                    }} className="overflow-hidden" data-id="element-1987">
                                <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-1988">
                                  <p className="font-nunito text-xs text-gray-500 leading-relaxed" data-id="element-1989">
                                    {translatedBio}
                                  </p>
                                  <a href={player.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 font-nunito text-[10px] font-bold text-haa-midblue hover:text-haa-blue transition-colors" data-id="element-1990">
                                    {t('news.player.viewProfile')}
                                    <ExternalLinkIcon className="w-2.5 h-2.5" data-id="element-1991" />
                                  </a>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>}
                {/* Video Embeds for Wabafu */}
                {post.videoEmbeds && post.videoEmbeds.length > 0 && <div className="mt-5 space-y-4" data-id="element-1992">
                    <div className="flex items-center gap-2 mb-2" data-id="element-1993">
                      <PlayCircleIcon className="w-4 h-4 text-amber-600" data-id="element-1994" />
                      <span className="font-nunito text-xs font-bold uppercase tracking-wider text-amber-700" data-id="element-1995">
                        {isEs ? 'Presentaciones' : 'Performances'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1996">
                      {post.videoEmbeds.map((video, vi) => <div key={vi} className="rounded-xl overflow-hidden shadow-md bg-black" data-id="element-1997">
                          {video.youtubeId ? <div className="relative w-full" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-1998">
                              <iframe src={`https://www.youtube.com/embed/${video.youtubeId}`} title={video.label} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-1999" />
                            </div> : video.facebookUrl ? <a href={video.facebookUrl} target="_blank" rel="noopener noreferrer" className="relative block w-full bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 transition-colors" style={{
                    paddingBottom: '56.25%'
                  }} data-id="element-2000">
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" data-id="element-2001">
                                <FacebookIcon className="w-8 h-8 text-blue-400" data-id="element-2002" />
                                <span className="font-nunito text-white text-xs font-semibold" data-id="element-2003">
                                  {video.label}
                                </span>
                                <span className="font-nunito text-white/50 text-[10px]" data-id="element-2004">
                                  {isEs ? 'Ver en Facebook' : 'Watch on Facebook'}
                                </span>
                              </div>
                            </a> : null}
                          <div className="px-3 py-2 bg-gray-900" data-id="element-2005">
                            <p className="font-nunito text-white/70 text-[10px] truncate" data-id="element-2006">
                              {video.label}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>}
                {/* Social Links for Wabafu */}
                {post.socialLinks && post.socialLinks.length > 0 && <div className="mt-5 pt-4 border-t border-gray-200" data-id="element-2007">
                    <span className="font-nunito text-xs font-bold uppercase tracking-wider text-gray-400 block mb-3" data-id="element-2008">
                      {isEs ? 'Síguelos' : 'Follow Them'}
                    </span>
                    <div className="flex items-center gap-3" data-id="element-2009">
                      {post.socialLinks.map((social, si) => <a key={si} href={social.url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-nunito text-xs font-bold transition-all duration-200 ${social.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white hover:shadow-lg hover:shadow-pink-500/25 hover:scale-105' : 'bg-[#1877F2] text-white hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105'}`} aria-label={social.label} data-id="element-2010">
                          {social.platform === 'instagram' ? <InstagramIcon className="w-4 h-4" data-id="element-2011" /> : <FacebookIcon className="w-4 h-4" data-id="element-2012" />}
                          {social.label}
                        </a>)}
                    </div>
                  </div>}
              </motion.div>}
          </AnimatePresence>
        </div>

        {post.fullStory && <button onClick={() => setStoryOpen(!storyOpen)} className="mb-3 inline-flex items-center gap-1.5 text-haa-blue font-nunito font-bold text-xs hover:text-haa-midblue transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue rounded self-start" aria-expanded={storyOpen} data-id="element-2013">
            {storyOpen ? t('news.collapseStory') : t('news.readFullStory')}
            <motion.span animate={{
          rotate: storyOpen ? 180 : 0
        }} transition={{
          duration: 0.25
        }} className="inline-flex" data-id="element-2014">
              <ChevronDownIcon className="w-3.5 h-3.5" data-id="element-2015" />
            </motion.span>
          </button>}

        {post.coverageLinks && post.coverageLinks.length > 0 && <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3" data-id="element-2016">
            <NewspaperIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-2017" />
            <span className="font-nunito text-gray-400 text-xs uppercase tracking-wider font-semibold" data-id="element-2018">
              {t('news.alsoCoveredBy')}
            </span>
            {post.coverageLinks.map((link, i) => <Fragment key={i} data-id="element-2019">
                {i > 0 && <span className="text-gray-200 select-none" data-id="element-2020">·</span>}
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-nunito text-xs font-bold text-haa-midblue hover:text-haa-blue hover:underline transition-colors" data-id="element-2021">
                  {link.label}
                  <ExternalLinkIcon className="w-3 h-3" data-id="element-2022" />
                </a>
              </Fragment>)}
          </div>}
      </div>

      <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-gray-100 mt-auto" data-id="element-2023">
        <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs" data-id="element-2024">
          <span className="flex items-center gap-1" data-id="element-2025">
            <CalendarIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-2026" />
            <span className="font-nunito" data-id="element-2027">{translatedDate}</span>
          </span>
        </div>
      </div>
    </motion.article>;
}
// Main Section
export function NewsBlog() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    t
  } = useLanguage();
  const filtered = activeCategory === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === activeCategory);
  const featured = filtered.find(p => p.featured) ?? null;
  const rest = filtered.filter(p => p.id !== featured?.id);
  const categoryTranslations: Record<string, string> = {
    All: t('news.catAll'),
    Political: t('news.catPolitical'),
    Immigration: t('news.catImmigration'),
    Business: t('news.catBusiness'),
    'Note Worthy': t('news.catNoteWorthy'),
    Events: t('news.catEvents'),
    'Arts & Entertainment': t('news.catArtMedia'),
    Sports: t('news.catSports')
  };
  return <section id="news" className="bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="news-heading" data-id="element-2028">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2029">
        <div ref={titleRef} className="mb-10" data-id="element-2030">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-2031">
            <div data-id="element-2032">
              <span className="font-nunito text-haa-midblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2033">
                {t('news.label')}
              </span>
              <h2 id="news-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2 leading-none" data-id="element-2034">
                {t('news.heading')}
              </h2>
              <div className="flex items-center gap-3 mt-4" data-id="element-2035">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2036" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-2037" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-2038" />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.15
      }} className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Filter by category" data-id="element-2039">
          {ALL_CATEGORIES.map(cat => <button key={cat} role="tab" aria-selected={activeCategory === cat} onClick={() => setActiveCategory(cat)} className={`relative font-nunito font-semibold text-sm px-5 py-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue ${activeCategory === cat ? 'bg-haa-navy text-white shadow-md' : 'bg-white text-gray-500 hover:text-haa-navy border border-gray-200 hover:border-haa-navy'}`} data-id="element-2040">
              <span className="inline-flex items-center gap-1.5" data-id="element-2041">
                {cat === 'Sports' && <TrophyIcon className="w-3.5 h-3.5" data-id="element-2042" />}
                {categoryTranslations[cat] ?? cat}
              </span>
            </button>)}
        </motion.div>

        <AnimatePresence mode="wait" data-id="element-2043">
          <motion.div key={activeCategory} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-2044">
            {filtered.length === 0 ? <div className="text-center py-20" data-id="element-2045">
                <TagIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-2046" />
                <p className="font-nunito text-gray-400 text-lg" data-id="element-2047">
                  {t('news.noPosts')}
                </p>
              </div> : activeCategory === 'All' ? <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2048">
                  {featured && <FeaturedCard post={featured} data-id="element-2049" />}
                  {rest.length > 0 && <div className="flex flex-col gap-6" data-id="element-2050">
                      {rest.slice(0, 2).map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-2051" />)}
                    </div>}
                </div>
                {rest.length > 2 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-id="element-2052">
                    {rest.slice(2).map((post, i) => <PostCard key={post.id} post={post} index={i + 2} data-id="element-2053" />)}
                  </div>}
              </> : <div className={`grid gap-6 ${filtered.length === 1 ? 'grid-cols-1 max-w-3xl mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`} data-id="element-2054">
                {filtered.map((post, i) => <PostCard key={post.id} post={post} index={i} data-id="element-2055" />)}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}